import { useEffect, useState, useRef } from 'react';
import { Box, Slider, Typography, makeStyles, withStyles } from '@material-ui/core';
import { convert_normalized_slider_positive_value } from '../../../helperFunctions';
import { addNameToText } from '../helperFunctions';
import IdealRange from '../../idealRange';

const useStyles = makeStyles(theme => ({
  mainContainer: {
    textAlign: 'center',
    width: '100%',
    margin: '0 auto',
    '@media (max-width: 768px)': {
      margin: 0,
      textAlign: 'left',
    },
    '@media (max-width: 600px)': {
      overflowX: 'auto',
      overflowY: 'hidden',
      paddingBottom: '16px',
    },
  },
  sliderGroupWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '8px 0',
    '@media (max-width: 800px)': {
      justifyContent: 'space-between',
    },
  },
  sliderElementsText: ({ questionIsEnabled }) => ({
    color: !questionIsEnabled ? theme.palette.text.disabled : theme.palette.text.primary,
    width: '200px',
    minWidth: '120px',
    fontWeight: 300,
    fontSize: '15px',
    textAlign: 'right',
    '@media (max-width: 1025px)': {
      fontSize: '14px !important',
    },
  }),
  text: ({ questionIsEnabled }) => ({
    color: !questionIsEnabled ? theme.palette.text.disabled : theme.palette.text.primary,
    fontWeight: 300,
    fontSize: '18px',
    maxWidth: '800px',
    margin: '0 auto 30px auto',
    margin: '0 auto 30px auto',
    '@media (max-width: 600px)': {
      fontSize: '16px',
      marginInline: 0,
    },
  }),
  slider: {
    width: '500px',

    '@media (max-width: 1100px)': {
      width: '300px',
    },
    '@media (max-width: 850px)': {
      width: '200px',
    },
  },
  allSlidersWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: '12px',
    position: 'relative',
  },
  sliderContainer: {
    height: '32px',
    position: 'relative',
    margin: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  zeroMarker: {
    position: 'absolute',
    height: '30px', // Full height of the slider
    width: '2.5px',
    backgroundColor: '#b8b8b8',
    left: '-2px',
    top: '4px',
    zIndex: 2,
  },
  idealRangeContainer: {
    height: '50px',
  },
}));

const styles = theme => ({
  root: {
    height: 5,
  },
  track: {
    height: 5,
    borderRadius: 4,
    backgroundColor: 'transparent',
    zIndex: 4,
  },
  rail: {
    height: 5,
    borderRadius: 4,
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 4,
  },
  thumb: props => ({
    height: 16,
    width: 16,
    zIndex: 5,
    backgroundColor: props.hasvalue === 'none' ? '#82C2C3' : '#19989B',
    boxShadow: '3px 3px 4px rgba(0,0,0,0.25)',
    transform: 'rotate(45deg) translateX(-1px) translateY(1px)',
    borderRadius: 0, // Makes it square
    '&:hover, &.Mui-active': {
      boxShadow: '3px 3px 4px rgba(0,0,0,0.25)',
    },
  }),
  active: {},
  valueLabel: {
    transform: 'rotate(-45deg)',
    top: '-32px',
    color: 'transparent',

    '& span span': {
      color: '#585858',
      fontSize: '15px',
    },
  },
});

const CustomSlider = withStyles(styles)(Slider);

const initialStateHandle = (id, value) => {
  let obj = {};
  for (let key of id) {
    const localValue = typeof value[key] !== 'undefined' && value[key] !== null ? value[key] : '';

    if (localValue !== '') {
      obj[key] = convert_normalized_slider_positive_value(localValue);
    } else {
      obj[key] = localValue;
    }
  }
  return obj;
};

const SurveyRangeSliderOnlyPositive = ({
  id,
  name,
  text,
  left,
  right,
  value,
  handleSurveyAnswers,
  dependancySelection,
  isInGroup,
  handleSliderDoubleClick,
}) => {
  const dependencyValueMatches = (depValue, currentValue) => {
    if (depValue === currentValue) return true;
    return Number(depValue) === Number(currentValue);
  };

  const questionIsEnabled = !dependancySelection
    ? true
    : dependancySelection.value.find(
        depValue => dependencyValueMatches(depValue, value[dependancySelection.dependancy_question_id]),
      );

  const classes = useStyles({ questionIsEnabled });

  const [selectedValue, setSelectedValue] = useState(() => initialStateHandle(id, value));

  useEffect(() => {
    setSelectedValue(() => initialStateHandle(id, value));
  }, [id]);

  const handleChange = (newValue, id) => {
    setSelectedValue(prevState => ({
      ...prevState,
      [id]: newValue,
    }));
  };

  const lastCommitRef = useRef({});
  const DOUBLE_COMMIT_DELAY = 265;
  const handleChangeCommittedWithReset = (newValue, sliderId) => {
    const now = Date.now();
    const lastCommit = lastCommitRef.current[sliderId] || 0;

    if (now - lastCommit < DOUBLE_COMMIT_DELAY) {
      handleSliderDoubleClick(sliderId);
      handleChange('', sliderId);

      lastCommitRef.current[sliderId] = 0;
    } else {
      lastCommitRef.current[sliderId] = now;
      handleSurveyAnswers(newValue, sliderId);
    }
  };

  const valueText = value => {
    return value ? `${Math.abs(value)}%` : '0%';
  };

  const valueLabelFormat = value => {
    return `${Math.abs(value)}%`;
  };

  const styleForGroup = isInGroup
    ? {
        marginBottom: '50px',
        paddingBottom: '50px',
        borderBottom: '1px solid lightgray',
      }
    : {};

  return (
    <Box className={classes.mainContainer} style={styleForGroup}>
      <Typography component="div" variant="h2" className={classes.text}>
        {addNameToText(text, name)}
      </Typography>
      <Box className={classes.allSlidersWrapper}>
        <Box className={classes.idealRangeContainer}>
          <IdealRange width="214px" type="survey-range-slider" />
        </Box>
        {left.map((option, index1) => {
          return (
            <Box key={index1} className={classes.sliderGroupWrapper}>
              <Typography
                component="div"
                variant="body2"
                className={classes.sliderElementsText}
                style={{ textAlign: 'right' }}>
                {addNameToText(option, name)}
              </Typography>
              <div className={classes.sliderContainer}>
                <CustomSlider
                  value={selectedValue[id[index1]] ? Math.round(selectedValue[id[index1]]) : 0}
                  hasvalue={
                    typeof selectedValue[id[index1]] === 'undefined' || selectedValue[id[index1]] === '' ? 'none' : 'yes'
                  }
                  min={0}
                  max={100}
                  step={1}
                  onChangeCommitted={(_, newValue) => handleChangeCommittedWithReset(newValue, id[index1])}
                  onChange={(_, newValue) => handleChange(newValue, id[index1])}
                  getAriaValueText={valueText}
                  aria-labelledby="slider-label"
                  valueLabelDisplay="on"
                  valueLabelFormat={valueLabelFormat}
                  className={classes.slider}
                  disabled={!questionIsEnabled}
                />
                <div className={classes.zeroMarker}></div>
              </div>
              <Typography
                component="div"
                variant="body2"
                className={classes.sliderElementsText}
                style={{ textAlign: 'left' }}>
                {addNameToText(right[index1], name)}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SurveyRangeSliderOnlyPositive;
